import { useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useRefetchOnResume } from '@/hooks/useRefetchOnResume';
import type { Poll, PollOption } from '@/lib/supabase-types';

const POLL_LIST_POLL_MS = 30_000;
// The poll row plus its whole ballot; anyone may read both tables.
const COLUMNS = 'id, club_id, title, status, category, poll_options(id, poll_id, label, sort_order, event_id, player_id, image_url, jersey_number, team_type)';

type ReadRow = Omit<Poll, 'options'> & { poll_options: (PollOption & { event_id: string | null })[] | null };

/**
 * The ballot the fan page RPC sends (get_fan_page_data): a preset poll's
 * options belong to the match on the page, a custom poll's carry no match.
 * The read brings every option; this keeps the right ones, in order.
 */
export function pollRowsFromRead(rows: ReadRow[], eventId: string | null | undefined): Poll[] {
  return rows.map(({ poll_options, ...poll }) => {
    const options = (poll_options ?? [])
      .filter((o) => (eventId ? o.event_id === eventId : false) || (o.event_id == null && poll.category === 'custom'))
      .sort((a, b) => a.sort_order - b.sort_order)
      .map(({ event_id: _eventId, ...option }) => option);
    return { ...poll, options };
  });
}

/**
 * The club's polls with their ballots, read directly. The realtime row flip
 * is what makes the vote pop up at once; this is the net under it, the same
 * one the quiz list has, and it also brings the ballot the flip cannot: the
 * fan page RPC ships only OPEN polls, so a poll that was a draft when the
 * page loaded reached the page as a bare row and stayed hidden ("open polls
 * with a real ballot") until a refresh. Found on the San Marino test, 4 Nov
 * 2026. Reads on mount, on resume, every 30 s, and whenever the page asks
 * (`refresh`, called from the realtime handler for a poll without a ballot).
 */
export function usePollListSync(
  clubId: string | null | undefined,
  eventId: string | null | undefined,
  onRows: (rows: Poll[]) => void,
  { enabled = true, intervalMs = POLL_LIST_POLL_MS }: { enabled?: boolean; intervalMs?: number } = {},
): { refresh: () => Promise<void> } {
  const onRowsRef = useRef(onRows);
  onRowsRef.current = onRows;
  const active = enabled && !!clubId;
  const read = useCallback(async () => {
    if (!active || !clubId) return;
    const { data, error } = await supabase.from('polls').select(COLUMNS).eq('club_id', clubId);
    if (error || !data) return;
    onRowsRef.current(pollRowsFromRead(data as unknown as ReadRow[], eventId));
  }, [active, clubId, eventId]);

  useEffect(() => { if (active) void read(); }, [active, read]);
  useRefetchOnResume(read, { enabled: active, intervalMs });
  return { refresh: read };
}

const sameOptions = (a: PollOption[], b: PollOption[]) =>
  a.length === b.length && a.every((o, i) => o.id === b[i].id && o.label === b[i].label && o.image_url === b[i].image_url);

/**
 * A fresh read into the page's list: status, title and ballot from the read;
 * a cached ballot survives a read that brings none (an option set the page
 * got from the RPC but the match filter here cannot see). Unknown polls join,
 * polls no longer in the read leave. Same array when nothing changed, so
 * nothing re-renders.
 */
export function mergePollRows(current: Poll[], rows: Poll[]): Poll[] {
  const next = rows.map((row) => {
    const existing = current.find((p) => p.id === row.id);
    const options = row.options.length > 0 ? row.options : (existing?.options ?? []);
    return existing && sameOptions(existing.options, options) ? { ...existing, ...row, options: existing.options } : { ...row, options };
  });
  const same = next.length === current.length && next.every((p, i) => {
    const c = current[i];
    return c.id === p.id && c.status === p.status && c.title === p.title && c.category === p.category && c.options === p.options;
  });
  return same ? current : next;
}
